import { useEffect } from 'react';
import type { ConsoleApiClient, ConsoleLogger, CookieApi, LocationApi, SystemTheme } from '../types';
import type { DispatchAction } from '../actions';
import type { State } from '../state';
import { isImpersonationActive } from '../domain/isImpersonationActive';
import { readInitialProductEnvironment } from '../domain/readInitialProductEnvironment';
import { detectActiveProduct } from '../domain/detectActiveProduct';
import { pickInitialTheme } from '../domain/pickInitialTheme';
import { createLogoUrlWithFallback } from '../domain/createLogoUrlWithFallback';

const ASSET_VISIBILITY_COOKIE_NAME = 'adminDisplayedImages';

export const useApplicationStartupFlow = ({
  consoleApiClient,
  consoleLogger,
  dispatch,
  state,
  getPageHref,
  localStorageClient,
  systemTheme,
  enableTheming,
  cookieApi,
}: {
  consoleApiClient: ConsoleApiClient;
  consoleLogger: ConsoleLogger;
  dispatch: DispatchAction;
  state: State;
  getPageHref: LocationApi['getHref'];
  localStorageClient: Storage;
  systemTheme: SystemTheme;
  enableTheming: boolean;
  cookieApi: CookieApi;
}) => {
  const startupStep = state.applicationStatus === 'startup' ? state.startupStep : undefined;

  useEffect(() => {
    if (startupStep?.name === 'detectingActiveProduct') {
      const detectionResult = detectActiveProduct(getPageHref());

      if (detectionResult.kind === 'success') {
        consoleLogger.debug('general', 'Startup step finished', {
          stepName: 'detectingActiveProduct',
          activeProductName: detectionResult.activeProductName,
        });
        dispatch({ type: 'applicationStartupActiveProductDetected', payload: { activeProductName: detectionResult.activeProductName } });
      }

      if (detectionResult.kind === 'error') {
        consoleLogger.debug('general', 'Startup failed', { stepName: 'detectingActiveProduct' });
        dispatch({
          type: 'applicationStartupFailed',
          payload: { error: { message: 'Startup failed - could not detect active product', stack: new Error().stack ?? '' } },
        });
      }
    }

    if (startupStep?.name === 'pickingInitialProductEnvironment') {
      const { activeProductName } = startupStep;
      const pickingResult = readInitialProductEnvironment(localStorageClient, getPageHref(), activeProductName);

      if (pickingResult.source === 'none') {
        void consoleApiClient.fetchAllProductEnvironments().then((result) => {
          if (result.kind === 'success') {
            consoleLogger.debug('general', 'Startup step finished', { stepName: 'pickingInitialProductEnvironment', pickingResult });
            dispatch({
              type: 'applicationStartupInitialProductEnvironmentSelected',
              payload: {
                // FIXME: types are not validating index access here
                productEnvironment: result.data[0],
                activeProductName,
              },
            });
          }

          if (result.kind === 'error') {
            consoleLogger.error('general', 'Startup failed', { stepName: 'pickingInitialProductEnvironment', pickingResult });
            dispatch({
              type: 'applicationStartupFailed',
              payload: { error: { message: result.errorMessage, stack: new Error().stack ?? '' } },
            });
          }
        });
      } else {
        void consoleApiClient.fetchProductEnvironment(pickingResult.externalId).then((result) => {
          if (result.kind === 'success') {
            consoleLogger.debug('general', 'Startup step finished', { stepName: 'pickingInitialProductEnvironment', pickingResult });
            dispatch({
              type: 'applicationStartupInitialProductEnvironmentSelected',
              payload: { activeProductName, productEnvironment: result.data },
            });
          }

          if (result.kind === 'error') {
            consoleLogger.error('general', 'Startup failed', { stepName: 'pickingInitialProductEnvironment', pickingResult });
            dispatch({
              type: 'applicationStartupFailed',
              payload: { error: { message: result.errorMessage, stack: new Error().stack ?? '' } },
            });
          }
        });
      }
    }

    if (startupStep?.name === 'fetchingInitialData') {
      const { activeProductName, productEnvironment } = startupStep;

      void Promise.all([
        consoleApiClient.fetchLoginFromSession(),
        consoleApiClient.fetchAccount(),
        consoleApiClient.fetchPlan(),
        consoleApiClient.fetchUser(),
      ]).then(([loginFromSessionResult, accountResult, planResult, userResult]) => {
        if (
          loginFromSessionResult.kind === 'success' &&
          accountResult.kind === 'success' &&
          planResult.kind === 'success' &&
          userResult.kind === 'success'
        ) {
          const theme = pickInitialTheme({
            systemTheme,
            enableTheming,
            userTheme: loginFromSessionResult.data.user.userTheme,
          });

          consoleLogger.debug('general', 'Startup step finished', { stepName: 'fetchingInitialData' });
          consoleLogger.debug('general', 'Application ready');
          dispatch({
            type: 'applicationStartupFinished',
            payload: {
              deprecatedData: {
                jwt: loginFromSessionResult.data.jwt,
                cmsMode: loginFromSessionResult.data.cmsMode,
                sessionNotifications: loginFromSessionResult.data.sessionNotifications,
                userEvents: loginFromSessionResult.data.userEvents,
                intercomHash: userResult.data.intercomHash,
                mediaLibraryPermissions: loginFromSessionResult.data.mediaLibraryPermissions,
                rootFolderRole: loginFromSessionResult.data.rootFolderRole,
              },
              user: userResult.data,
              capabilities: loginFromSessionResult.data.capabilities,
              permissions: loginFromSessionResult.data.permissions,
              isImpersonationActive: isImpersonationActive({
                productEnvironmentAccountId: loginFromSessionResult.data.productEnvironment.accountId,
                userAccountId: loginFromSessionResult.data.user.accountId,
              }),
              account: accountResult.data,
              plan: planResult.data,
              activeProductName,
              productEnvironment,
              theme,
              mainLogoUrl: createLogoUrlWithFallback(accountResult.data.id),
              assetVisibility: cookieApi.get(ASSET_VISIBILITY_COOKIE_NAME) === 'yes' ? 'visible' : 'notVisible',
            },
          });
        } else {
          consoleLogger.error('general', 'Startup failed - fetching initial data');
          dispatch({
            type: 'applicationStartupFailed',
            payload: {
              error: {
                message: 'Could not load initial data!',
                data: {
                  loginFromSession: loginFromSessionResult.kind === 'error' ? loginFromSessionResult : 'success',
                  account: accountResult.kind === 'error' ? accountResult : 'success',
                  plan: planResult.kind === 'error' ? planResult : 'success',
                },
                stack: new Error().stack ?? '',
              },
            },
          });
        }
      });
    }
  }, [consoleApiClient, consoleLogger, dispatch, enableTheming, getPageHref, localStorageClient, startupStep, systemTheme, cookieApi]);
};
